import React from 'react';
import { format } from 'date-fns';

import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';

import { useQuery } from '@tanstack/react-query';
import { fetcher } from '@dashboard/services/request';
import Spinner from '@dashboard/components/Spinner';
import StatusBadge from '@dashboard/components/StatusBadge';
import Button from '@dashboard/components/Button';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import Callout from '@dashboard/components/Callout';
import getStripeDashboardUrl from '@dashboard/lib/stripe/get-stripe-dashboard-url';
import CardListItem from '@dashboard/components/CardListItem';
import CardListItemElement from '@dashboard/components/CardListItemElement';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import Tooltip from '@dashboard/components/Tooltip';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import PaymentsSubscriptionPaymentRefundDialog from '../components/PaymentsSubscriptionPaymentRefundDialog';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

const SUBSCRIPTION_PAYMENT_STATUS_LABELS = {
  succeeded: 'Paid',
  pending: 'Pending',
  failed: 'Failed',
  incomplete: 'Incomplete',
};

function getSubscriptionPaymentStatus(payment) {
  if (payment.refunded) return 'refunded';
  if (payment.partially_refunded) return 'partially_refunded';
  if (payment.paid) return 'paid';

  return payment.paymentStatus;
}

function getSubscriptionPaymentStatusLabel(payment) {
  if (payment.refunded) return 'Refunded';
  if (payment.partially_refunded) return 'Partially refunded';
  if (payment.paid) return 'Paid';

  return SUBSCRIPTION_PAYMENT_STATUS_LABELS[payment.paymentStatus];
}

const PaymentsSubscriptionsDetailPayments = ({ subscription, subscriptionId }) => {
  const { token, seller, isAdminLoggedInAsSeller } = useAuth();

  const {
    data: subscriptionPayments,
    error: subscriptionPaymentsError,
    isLoading: isLoadingSubscriptionPayments,
  } = useQuery({
    queryKey: ['subscriptionPayments', subscriptionId],
    queryFn: () =>
      fetcher(`/sellers/${subscription.seller}/subscriptions/${subscriptionId}/payments`, token),
  });

  return (
    <div className="flex flex-col lg:flex-row mx-auto items-start justify-center">
      <div className="order-2 lg:order-1 w-full lg:pr-8 px-4">
        <Card className="mx-auto !px-0 !py-2">
          <CardHeader>
            <CardHeaderTitle>Payments</CardHeaderTitle>
          </CardHeader>

          <CardContent>
            {isLoadingSubscriptionPayments && <Spinner />}

            {subscriptionPaymentsError && (
              <Callout variant="error">Failed to load subscription payments</Callout>
            )}

            {subscriptionPayments?.length === 0 && (
              <Callout variant="info">No payments found for this subscription.</Callout>
            )}

            {subscriptionPayments?.length > 0 &&
              (() => {
                return (
                  <div className="overflow-auto">
                    <CardListItem variant="header" className="!mt-0">
                      <CardListItemElement size="fill" className="!pl-0">
                        Amount
                      </CardListItemElement>
                      <CardListItemElement align="right" size="xl">
                        Date
                      </CardListItemElement>
                      <CardListItemElement size="large">Status</CardListItemElement>
                      <CardListItemElement size="4xl">Invoice</CardListItemElement>
                      <CardListItemElement size="xl" className="!pr-0"></CardListItemElement>
                    </CardListItem>

                    {subscriptionPayments.map(payment => (
                      <CardListItem key={payment._id}>
                        <CardListItemElement size="fill" className="!pl-0">
                          {getPriceLabel({
                            amount: payment.amount,
                            currency: subscription.currency,
                          })}
                        </CardListItemElement>
                        <CardListItemElement align="right" size="xl">
                          {format(new Date(payment.createdAt), 'd MMM, yyyy, HH:mm')}
                        </CardListItemElement>
                        <CardListItemElement size="large">
                          <StatusBadge
                            status={getSubscriptionPaymentStatus(payment)}
                            label={getSubscriptionPaymentStatusLabel(payment)}
                          />
                        </CardListItemElement>
                        <CardListItemElement size="4xl" className="!pr-0">
                          <Button
                            appearance="secondary"
                            size="small"
                            href={payment.stripeInvoicePdf}
                            blank
                            className="gap-x-2"
                          >
                            Download
                            <ArrowDownTrayIcon className="w-4 h-4" />
                          </Button>
                          {payment.stripeRefundInvoicePdf && (
                            <Button
                              className="ml-2 gap-x-2"
                              appearance="secondary"
                              size="small"
                              href={payment.stripeRefundInvoicePdf}
                              blank
                            >
                              Download refund
                              <ArrowDownTrayIcon className="w-4 h-4" />
                            </Button>
                          )}
                        </CardListItemElement>
                        <CardListItemElement size="xl" className="!pr-0">
                          <div className="flex gap-x-2 justify-end">
                            <PaymentsSubscriptionPaymentRefundDialog
                              payment={payment}
                              subscription={subscription}
                            />
                            <Tooltip label="View payment in Stripe">
                              <Button
                                appearance="secondary"
                                size="small"
                                href={getStripeDashboardUrl(
                                  `/payments/${payment.stripePaymentId || payment.stripePaymentIntentId}`,
                                  {
                                    seller,
                                    isAdminLoggedInAsSeller,
                                  },
                                )}
                                blank
                              >
                                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                              </Button>
                            </Tooltip>
                          </div>
                        </CardListItemElement>
                      </CardListItem>
                    ))}
                  </div>
                );
              })()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PaymentsSubscriptionsDetailPayments;
